import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { partnersApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { TextArea, TextField } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { toast } from '@/store/toast'
import type { CreatePartnerRequest } from '@/types/api'

import { copyText, generatePassword, suggestTag } from './format'
import { PasswordInput } from './ui'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** A new partner login. The password is shown once, with Copy, so it can be handed over. */
export function AddPartnerModal({ open, onClose, existingTags }: { open: boolean; onClose: () => void; existingTags: string[] }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [form, setForm] = useState(() => blank(existingTags))
  const [touched, setTouched] = useState(false)
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setForm(blank(existingTags))
      setTouched(false)
      setServerErrors({})
    }
    // Only when it opens: a refetch of the list must not wipe what is typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const set = (key: keyof typeof form) => (value: string) => {
    setForm((old) => ({ ...old, [key]: value }))
    setServerErrors((old) => ({ ...old, [key]: '' }))
  }

  const max = Number(form.maxDiscountPct)
  const errors: Record<string, string | null> = {
    name: form.name.trim() ? null : 'A name is needed.',
    email: EMAIL.test(form.email.trim()) ? null : 'A valid email is needed.',
    password: form.password.length >= 12 ? null : 'At least 12 characters.',
    maxDiscountPct: form.maxDiscountPct.trim() !== '' && Number.isFinite(max) && max >= 0 && max <= 100 ? null : 'A number from 0 to 100.',
    tag: !form.tag.trim() || /^[A-Z0-9-]{1,12}$/.test(form.tag.trim()) ? null : 'Up to 12 of A–Z, 0–9 and -.',
  }
  const invalid = Object.values(errors).some(Boolean)
  const show = (key: string) => serverErrors[key] || (touched ? errors[key] : null) || null

  const create = useMutation({
    mutationFn: (body: CreatePartnerRequest) => partnersApi.create(body),
    onSuccess: async (partner) => {
      void queryClient.invalidateQueries({ queryKey: ['partners'] })
      const copied = await copyText(`${form.email.trim()}\n${form.password}`)
      toast.success(`${partner.name || form.name} added`, copied ? 'Email and password copied. They sign in at partner.jobslide.app.' : 'They sign in at partner.jobslide.app.')
      onClose()
      if (partner.id) navigate(`/partners/${partner.id}`)
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        if (error.code === 'partner_email_taken' || error.status === 409) {
          setServerErrors({ email: error.code === 'partner_email_taken' ? 'A partner with this email already exists.' : error.message })
          return
        }
        if (Object.keys(error.fieldErrors).length) {
          setServerErrors(error.fieldErrors)
          return
        }
      }
      toast.error('Could not add the partner', error instanceof ApiError ? error.message : undefined)
    },
  })

  const submit = () => {
    setTouched(true)
    if (invalid) return
    create.mutate({
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.password,
      maxDiscountPct: max,
      ...(form.company.trim() && { company: form.company.trim() }),
      ...(form.tag.trim() && { tag: form.tag.trim() }),
      ...(form.phone.trim() && { phone: form.phone.trim() }),
      ...(form.notes.trim() && { notes: form.notes.trim() }),
    })
  }

  return (
    <Modal
      open={open}
      onClose={() => !create.isPending && onClose()}
      title="Add a partner"
      description="They sign in at partner.jobslide.app with this email and password, and make codes up to their maximum discount."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button loading={create.isPending} onClick={submit}>
            Add partner
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4 pb-3 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <TextField label="Name" required appearance="box" className="text-[14px]" value={form.name} onChange={(e) => set('name')(e.target.value)} error={show('name')} placeholder="Ravi Kumar" autoFocus />
        <TextField label="Company" appearance="box" className="text-[14px]" value={form.company} onChange={(e) => set('company')(e.target.value)} placeholder="Ravi Digital (optional)" />
        <div className="sm:col-span-2">
          <TextField label="Email" required appearance="box" type="email" className="text-[14px]" value={form.email} onChange={(e) => set('email')(e.target.value)} error={show('email')} placeholder="partner@example.com" autoComplete="off" />
        </div>
        <div className="sm:col-span-2">
          <PasswordInput value={form.password} onChange={set('password')} required error={show('password')} hint="Copied with the email when you add the partner. They can change it after signing in." />
        </div>
        <TextField
          label="Tag"
          appearance="box"
          className="num text-[14px] uppercase"
          value={form.tag}
          onChange={(e) => set('tag')(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
          maxLength={12}
          error={show('tag')}
          hint={<>Ends their suggested codes, e.g. IPHONE-{form.tag || 'P1'}.</>}
        />
        <TextField
          label="Max discount %"
          required
          appearance="box"
          type="number"
          inputMode="decimal"
          min={0}
          max={100}
          step={0.5}
          className="num text-[14px]"
          value={form.maxDiscountPct}
          onChange={(e) => set('maxDiscountPct')(e.target.value)}
          placeholder="30"
          error={show('maxDiscountPct')}
          hint="Coupon % + their commission % = this."
        />
        <TextField label="Phone" appearance="box" type="tel" className="text-[14px]" value={form.phone} onChange={(e) => set('phone')(e.target.value)} placeholder="Optional" />
        <div className="sm:col-span-2">
          <TextArea label="Notes" appearance="box" rows={2} className="text-[14px]" value={form.notes} onChange={(e) => set('notes')(e.target.value)} placeholder="Deal terms, contact person (only admins see this)" />
        </div>
        <button type="submit" hidden />
      </form>
    </Modal>
  )
}

function blank(tags: string[]) {
  return { name: '', company: '', email: '', password: generatePassword(), tag: suggestTag(tags), maxDiscountPct: '', phone: '', notes: '' }
}
